import { Component, OnInit, AfterViewInit, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AspectEvaluationComponent } from '../../components/aspect-evaluation/aspect-evaluation.component';
import { FileViewerComponent } from '../../components/file-viewer/file-viewer.component';
import { AspectEvaluationGroupComponent } from '../../components/aspect-evaluation-group/aspect-evaluation-group.component';
import { BannerComponent } from '@app/components/banner/banner.component';
import {
	Aspect,
	Submission,
	CompetencyLevel,
	FactorEvaluationRequestBody,
	ValidityOptions,
} from '../../models/evaluation.model';
import { ApiService } from '../../services/api.service';
import { ToastService } from '../../services/toast.service';
import {
	EvaluationResponse,
	FactorEvaluation,
	StatusType,
	TaskDetail,
	TaskSummary,
} from '../../models/halosightDTO.model';
import { AxiosError } from 'axios';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';

@Component({
	selector: 'app-home',
	standalone: true,
	imports: [
		CommonModule,
		AspectEvaluationComponent,
		FileViewerComponent,
		AspectEvaluationGroupComponent,
		MatIconModule,
		BannerComponent,
	],
	templateUrl: './home.component.html',
	styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit {
	@Input() evaluationId: string = ''; // sample id josh-test-id-1
	@Input() taskId: string = ''; // sample id test_rgm3_task1_id_1
	aspects: Aspect[] = [];
	tasks: TaskDetail[] = [];
	activeAspectIds: string[] = [];
	submission: Submission | null = null;
	@ViewChild('aspectGroup') aspectGroup!: AspectEvaluationGroupComponent;
	constructor(
		private apiService: ApiService,
		private toast: ToastService,
		private route: ActivatedRoute,
	) {}
	isLoading: boolean = false;
	isSaving: boolean = false;
	failedToExit: boolean = false;
	allAspectsCompleted: boolean = false;
	failedSaveAspectsList: Aspect[] = [];
	showAllAspects: Boolean = false;
	hasError: Boolean = false;

	get displayedAspects() {
		return this.showAllAspects
			? this.aspects
			: this.aspects.filter(x => x.status.name === 'Open');
	}

	ngOnInit() {
		this.getEvaluationResults();
	}

	saveAndExit(exit: boolean = true) {
		this.apiService.instance
			.post(`/api/evaluation-results/v1/eval-complete`, null, {
				params: {
					'submission-id': this.evaluationId,
					'external-task-id': this.taskId,
				},
			})
			.then(() => {
				if (exit) {
					window.close();
					if (!window.closed) {
						this.failedToExit = true;
					}
				}
			})
			.catch(err => {
				console.error(err);
				this.toast.showError(
					'An error occurred while completing the evaluation. Please try again later.',
				);
			});
	}

	getEvaluationResults() {
		this.isLoading = true;
		this.hasError = false;
		if (this.evaluationId && this.taskId) {
			this.apiService.instance
				.get<EvaluationResponse>(
					`/api/evaluation-results/v1/${this.evaluationId}/${this.taskId}`,
				)
				.then(resp => {
					this.aspects = this.formatAspects(resp.data.factorEvaluations);
					this.submission = this.formatSubmission(resp.data);
					this.getAspectDetails();
				})
				.catch(err => {
					console.error(err);
					this.hasError = true;
				});
		} else {
			this.toast.showError('Invalid Task Id Provided');
		}
	}

	getAspectDetails() {
		if (this.taskId) {
			this.apiService.instance
				.get<TaskSummary>(
					`/api/internal/metadata/v1/task/json/summary?external-id=${this.taskId}`,
				)
				.then(resp => {
					resp.data.factors.forEach(factor => {
						const aspectIndex = this.aspects.findIndex(x => x.factorId === factor.id);
						if (aspectIndex !== -1) {
							this.aspects[aspectIndex] = {
								...this.aspects[aspectIndex],
								name: factor.name,
								description: factor.description,
								suitability:
									factor.suitability === 'UNKNOWN_SUITABILITY'
										? 'ABSENT'
										: factor.suitability,
								competencyDescriptions: factor.qualitySpecs.map(x => {
									return {
										description: x.criteria,
										competencyLevel: x.qualityLevelLabel as CompetencyLevel,
									};
								}),
							};
						}
					});
					this.aspects.sort((a, b) => {
						if (!a.name && !b.name) {
							return 0;
						} else if (!a.name) {
							return 1;
						} else if (!b.name) {
							return -1;
						} else {
							return a.name.localeCompare(b.name);
						}
					});
					this.isLoading = false;
				})
				.catch((err: AxiosError) => {
					this.hasError = true;
				});
		}
	}

	aspectGroupInitialized() {
		this.validateAspectCompleteness({
			ignoredCompetencyLevels: ['pending'],
		});
	}

	toggleAspectVisibility(visible: boolean) {
		this.showAllAspects = visible;
	}

	private validateAspectCompleteness(options: ValidityOptions = {}) {
		this.allAspectsCompleted = this.aspectGroup.validate(options);
		return this.allAspectsCompleted;
	}

	private formatSubmission(evaluationResponse: EvaluationResponse) {
		return {
			id: evaluationResponse.externalSubmissionId,
			// TODO: Add Student Id?
			studentId: 'StudentTest',
			taskId: evaluationResponse.externalTaskId,
			files: evaluationResponse.artifactInfos.map(artifact => {
				return {
					id: artifact.id,
					name: artifact.externalFileId || 'Document',
					annotatedText: artifact.annotatedText,
					type: 'text/html',
				};
			}),
		};
	}

	private formatAspects(factors: FactorEvaluation[]): Aspect[] {
		const factorMap = new Map<string, Aspect>();
		factors.forEach(factor => {
			let aspect: Aspect = factorMap.has(factor.factorId)
				? factorMap.get(factor.factorId)!
				: {
						id: factor.id,
						factorId: factor.factorId,
						name: `Factor: ${factor.factorId}`,
						status: { name: 'Open' },
					};

			const completedStatusIndex = factor.fieldValues.findIndex(
				x => x.fieldType.name === 'wgu-aspect-status',
			);

			if (completedStatusIndex !== -1) {
				aspect.status.name = 'Locked';
				aspect.status.value = factor.fieldValues[completedStatusIndex].value as StatusType;
			}

			if (aspect.status.name === 'Open') {
				if (factor.evaluator.type === 'AI') {
					aspect = {
						...aspect,
						aiSuggestedCompetencyLevel: factor.qualityLevel as CompetencyLevel,
					};
					factor.fieldValues.forEach(fieldValue => {
						switch (fieldValue.fieldType.name) {
							case 'improvement_suggestion':
								aspect.aiSuggestions = fieldValue.value;
								break;
							case 'positive_feedback':
								aspect.aiAnalysis = fieldValue.value;
								break;
						}
					});
				} else if (factor.evaluator.type === 'HUMAN') {
					aspect = {
						...aspect,
						competencyLevel: factor.qualityLevel as CompetencyLevel,
					};
					factor.fieldValues.forEach(fieldValue => {
						switch (fieldValue.fieldType.name) {
							case 'human-feedback':
								aspect.feedback = fieldValue.value;
								break;
						}
					});
				}
			}

			factorMap.set(factor.factorId, aspect);
		});
		return Array.from(factorMap.values());
	}

	updateActiveAspects(activeAspects: string[]) {
		this.activeAspectIds = activeAspects;
	}

	onAspectChange(aspect: Aspect): void {
		const index = this.aspects.findIndex(x => x.id === aspect.id);
		this.aspects[index] = aspect;
		const requestBody: FactorEvaluationRequestBody = {
			factorId: aspect.factorId,
			qualityLevel: aspect.competencyLevel,
			fields: [
				{
					fieldTypeName: 'human-feedback',
					value: aspect.feedback,
				},
			],
		};
		this.isSaving = true;

		this.apiService.instance
			.post(
				`/api/evaluation-results/v1/human-factor-evaluations/${this.evaluationId}/${this.taskId}`,
				requestBody,
			)
			.then(resp => {
				if (this.failedSaveAspectsList.length > 0) {
					const index = this.failedSaveAspectsList.findIndex(x => x.id === aspect.id);
					if (index !== -1) {
						this.failedSaveAspectsList.splice(index, 1);
					}
				}
			})
			.catch(err => {
				this.toast.showError('Error saving feedback. ' + err);

				const index = this.failedSaveAspectsList.findIndex(x => x.id === aspect.id);
				// Remove duplicate instances of failed saves
				if (index !== -1) {
					this.failedSaveAspectsList.splice(index, 1);
				}
				this.failedSaveAspectsList.push(aspect);
			})
			.finally(() => {
				this.isSaving = false;
			});
	}

	retryFailedSaves() {
		this.failedSaveAspectsList.forEach(aspect => {
			this.onAspectChange(aspect);
		});
	}

	aspectAnalysisComplete() {
		if (this.validateAspectCompleteness()) {
			this.toast.showSuccess('Aspect analysis complete');
		} else {
			this.toast.showError('One or more aspects have not been completed');
		}
	}
}
